import React from 'react'
import { motion } from 'framer-motion'
import { 
  Shield,
  Lock,
  Eye,
  Database,
  Clock,
  FileText,
  CheckCircle
} from 'lucide-react'

const PrivacyScreen: React.FC = () => {
  const sections = [
    {
      id: 'introduction',
      title: 'Giriş',
      icon: FileText,
      content: [
        'VentraWind olarak, kişisel verilerinizin korunması konusunda hassasiyetle yaklaşıyoruz. Bu Gizlilik Politikası, kişisel verilerinizi nasıl topladığımız, kullandığımız, sakladığımız ve koruduğumuz hakkında bilgi vermektedir. 6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) ve Avrupa Birliği Genel Veri Koruma Yönetmeliği (GDPR) hükümlerine uygun olarak hazırlanmıştır.'
      ]
    },
    {
      id: 'data-controller',
      title: 'Veri Sorumlusu',
      icon: Shield,
      content: [
        'Veri sorumlusu olarak VentraWind Teknoloji A.Ş., Adalet Mahallesi, Manas Bulvarı No: 47/A, Folkart Towers A Kule Kat: 12, 35535 Bayraklı/İZMİR adresinde faaliyet göstermekte olup, kişisel verilerinizin işlenmesi konusunda karar verme yetkisine sahiptir.'
      ]
    },
    {
      id: 'data-collection',
      title: 'Kişisel Veri Toplama',
      icon: Database,
      content: [
        'Hizmetlerimizi sunabilmek için aşağıdaki kişisel verilerinizi toplayabiliriz:',
        '• Kimlik bilgileri (ad, soyad, e-posta adresi)',
        '• İletişim bilgileri (telefon numarası, adres)',
        '• Teknik veriler (IP adresi, çerez bilgileri)',
        '• Kullanım verileri (platform etkileşimleri)'
      ]
    },
    {
      id: 'data-usage',
      title: 'Veri Kullanım Amaçları',
      icon: Eye,
      content: [
        'Kişisel verilerinizi aşağıdaki amaçlarla işlemekteyiz:',
        '• Hizmet sunumu ve müşteri destek hizmetleri',
        '• Yasal yükümlülüklerin yerine getirilmesi',
        '• İstatistiksel analiz ve raporlama',
        '• Güvenlik ve dolandırıcılık önleme',
        '• Pazarlama ve iletişim faaliyetleri (onayınız dahilinde)'
      ]
    },
    {
      id: 'data-sharing',
      title: 'Veri Paylaşımı',
      icon: Lock,
      content: [
        'Kişisel verilerinizi aşağıdaki durumlarda üçüncü taraflarla paylaşabiliriz:',
        '• Yasal zorunluluklar çerçevesinde',
        '• Hizmet sağlayıcılarımızla (veri işleme sözleşmesi kapsamında)',
        '• İş ortaklarımızla (açık rızanız dahilinde)',
        '• Mahkeme kararları ve yasal süreçler gereği'
      ]
    },
    {
      id: 'data-security',
      title: 'Veri Güvenliği',
      icon: Shield,
      content: [
        'Kişisel verilerinizin güvenliği için aşağıdaki önlemleri almaktayız:',
        '• SSL şifreleme teknolojisi',
        '• Güvenli sunucu altyapısı',
        '• Erişim kontrolü ve yetkilendirme',
        '• Düzenli güvenlik denetimleri',
        '• Personel eğitimi ve farkındalık programları'
      ]
    },
    {
      id: 'data-retention',
      title: 'Veri Saklama Süresi',
      icon: Clock,
      content: [
        'Kişisel verilerinizi işleme amacının ortadan kalkması, yasal saklama sürelerinin dolması veya talebiniz üzerine sileriz. Genel saklama süreleri:',
        '• Müşteri verileri: Hizmet süresi + 10 yıl',
        '• İletişim kayıtları: 3 yıl',
        '• Teknik loglar: 1 yıl',
        '• Pazarlama verileri: Onay iptali + 1 yıl'
      ]
    },
    {
      id: 'your-rights',
      title: 'Haklarınız',
      icon: CheckCircle,
      content: [
        'KVKK kapsamında sahip olduğunuz haklar:',
        '• Kişisel verilerinizin işlenip işlenmediğini öğrenme',
        '• İşlenen kişisel verileriniz hakkında bilgi talep etme',
        '• İşleme amacını ve bunların amacına uygun kullanılıp kullanılmadığını öğrenme',
        '• Yurt içinde veya yurt dışında kişisel verilerin aktarıldığı üçüncü kişileri bilme',
        '• Kişisel verilerin eksik veya yanlış işlenmiş olması halinde bunların düzeltilmesini isteme',
        '• Kişisel verilerin silinmesini veya yok edilmesini isteme',
        '• Düzeltme, silme ve yok etme işlemlerinin kişisel verilerin aktarıldığı üçüncü kişilere bildirilmesini isteme',
        '• İşlenen verilerin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle kişinin aleyhine bir sonucun ortaya çıkmasına itiraz etme',
        '• Kişisel verilerin kanuna aykırı olarak işlenmesi sebebiyle zarara uğraması halinde zararın giderilmesini talep etme'
      ]
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Shield className="w-16 h-16 mx-auto mb-6 text-primary-200" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Gizlilik Politikası
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              Kişisel verilerinizin korunması bizim için önemlidir. Bu politika, verilerinizi nasıl topladığımız, 
              kullandığımız ve koruduğumuz hakkında detaylı bilgi sağlar.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-sm border p-8"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      <section.icon className="w-6 h-6 text-primary-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      {section.title}
                    </h2>
                    <div className="prose prose-gray max-w-none">
                      {section.content.map((paragraph, pIndex) => (
                        <p key={pIndex} className="text-gray-600 leading-relaxed mb-3 last:mb-0">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    </div>
  )
}

export default PrivacyScreen 
 